import { Component, inject, OnInit, computed, signal, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TrainingProgressService } from '../../services/training-progress.service';
import { SafeHtmlPipe } from '../../shared/pipes/safe-html.pipe';
import { Icons } from '../../shared/icons';
import { Module, Section } from '../../models/programme.model';
import { TranslatePipe } from '../../../filters/translate.pipe';

@Component({
  selector: 'app-module-lesson',
  standalone: true,
  imports: [CommonModule, SafeHtmlPipe, TranslatePipe],
  templateUrl: './module-lesson.component.html',
  styleUrls: ['../../training-styles.css'],
  encapsulation: ViewEncapsulation.None
})
export class ModuleLessonComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private trainingProgressService = inject(TrainingProgressService);

  readonly Icons = Icons;

  selectedProgramVersionCode = '';
  mod!: Module;
  moduleIndex = 0;
  currentSection?: Section;
  currentSectionIndex = 0;

  private _refresh = signal(0);

  readonly allViewed = computed(() => {
    this._refresh(); // trigger reactivity
    return this.mod ? this.trainingProgressService.allSectionsViewed(this.mod.id) : false;
  });

  ngOnInit(): void {
    this.selectedProgramVersionCode = this.route.snapshot.queryParamMap.get('programVersionCode') || '';
    this.trainingProgressService.setProgramVersionCode(this.selectedProgramVersionCode);

    this.route.params.subscribe(async params => {
      const moduleId = params['moduleId'];
      const sectionId = params['sectionId'];

      const detail = await this.trainingProgressService.loadModuleDetail(moduleId);
      if (!detail || !detail.module) {
        const queryParams = this.selectedProgramVersionCode ? { programVersionCode: this.selectedProgramVersionCode } : undefined;
        this.router.navigate(['/my-training', 'overview'], { queryParams });
        return;
      }
      this.mod = { ...(detail.module || {}), sections: detail.sections || [] } as any;
      const modules = await this.trainingProgressService.loadModules();
      const idx = modules.findIndex(m => (m.module?.id || m.module?.moduleId || m.moduleId) == moduleId);
      this.moduleIndex = idx >= 0 ? idx : 0;
      await this.trainingProgressService.loadSectionViews(moduleId);

      this.currentSection = sectionId
        ? detail.sections.find((s: any) => s.id == sectionId) || detail.sections[0]
        : detail.sections[0];
      this.currentSectionIndex = this.currentSection ? detail.sections.indexOf(this.currentSection) : 0;
      if (this.currentSection) {
        await this.trainingProgressService.markSectionViewed(moduleId, this.currentSection.id);
      }
      this._refresh.update(v => v + 1);
      window.scrollTo(0, 0);
    });
  }

  isSectionViewed(sectionId: string): boolean {
    return this.trainingProgressService.hasViewedSection(this.mod.id, sectionId);
  }

  isSectionActive(sectionId: string): boolean {
    return this.currentSection?.id === sectionId;
  }

  navigate(path: string): void {
    const parts = path.startsWith('/') ? path.split('/').filter(Boolean) : [path];
    const queryParams = this.selectedProgramVersionCode ? { programVersionCode: this.selectedProgramVersionCode } : undefined;
    this.router.navigate(['/my-training', ...parts], { queryParams });
  }

  goToSection(sectionId: string): void {
    const queryParams = this.selectedProgramVersionCode ? { programVersionCode: this.selectedProgramVersionCode } : undefined;
    this.router.navigate(['/my-training', 'module', this.mod.id, sectionId], { queryParams });
  }

  goToPrev(): void {
    if (this.currentSectionIndex > 0) {
      this.goToSection(this.mod.sections[this.currentSectionIndex - 1].id);
    }
  }

  goToNext(): void {
    if (this.currentSectionIndex < this.mod.sections.length - 1) {
      this.goToSection(this.mod.sections[this.currentSectionIndex + 1].id);
    }
  }

  goToQuiz(): void {
    const queryParams = this.selectedProgramVersionCode ? { programVersionCode: this.selectedProgramVersionCode } : undefined;
    this.router.navigate(['/my-training', 'quiz', this.mod.id], { queryParams });
  }

  goToOverview(): void {
    const queryParams = this.selectedProgramVersionCode ? { programVersionCode: this.selectedProgramVersionCode } : undefined;
    this.router.navigate(['/my-training', 'overview'], { queryParams });
  }

  protected readonly String = String;
}
